/**
 * tdesign v0.44.2
 * (c) 2022 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-74ef9ead.js';
import { _ as _typeof } from '../_chunks/dep-dfae10c3.js';
import { _ as _slicedToArray } from '../_chunks/dep-784849f3.js';
import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import Portal from '../common/Portal.js';
import noop from '../_util/noop.js';
import useDialogEsc from '../_util/useDialogEsc.js';
import { dialogDefaultProps } from './defaultProps.js';
import { useLocaleReceiver } from '../locale/LocalReceiver.js';
import useDialogLockStyle from './hooks/useLockStyle.js';
import '../_chunks/dep-d6cc3db8.js';
import 'react-dom';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-10140b37.js';
import '../_chunks/dep-7a9656a6.js';
import '../_chunks/dep-da112583.js';
import '../_chunks/dep-1792ab93.js';
import '../_chunks/dep-1e4c771d.js';
import '../_util/easing.js';
import '../hooks/useConfig.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-5eadc4f3.js';
import '../_chunks/dep-fd215ab0.js';
import '../_chunks/dep-8127363b.js';
import '../_chunks/dep-a0b71ae4.js';
import '../_chunks/dep-670cc86e.js';
import '../_chunks/dep-f4495ac9.js';
import '../_chunks/dep-6c09308a.js';
import '../_chunks/dep-4ca52001.js';
import '../_chunks/dep-408df89a.js';
import '../_chunks/dep-e755a9cb.js';
import '../_chunks/dep-15d0e734.js';
import '../_chunks/dep-b0c02324.js';
import '../_chunks/dep-a09b6dac.js';
import '../_chunks/dep-9c2f4c72.js';
import '../_chunks/dep-da7344ec.js';
import '../_chunks/dep-01fe0fd1.js';
import '../_chunks/dep-20a8e46d.js';
import '../_chunks/dep-9d067299.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import '../_common/js/global-config/default-config.js';
import '../_chunks/dep-0508545e.js';
import '../config-provider/index.js';
import '../config-provider/ConfigProvider.js';
import '../_util/useLayoutEffect.js';
import '../_common/js/utils/getScrollbarWidth.js';
import '../_common/js/utils/helper.js';
import '../_chunks/dep-5e9bbb71.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function GetCSSValue(v) {
  return Number.isNaN(Number(v)) ? v : "".concat(Number(v), "px");
}
var transitionTime = 300;
var mousePosition;
var getClickPosition = function getClickPosition(e) {
  mousePosition = {
    x: e.clientX,
    y: e.clientY
  };
  setTimeout(function () {
    mousePosition = null;
  }, 100);
};
if (typeof window !== "undefined" && window.document && window.document.documentElement) {
  document.documentElement.addEventListener("click", getClickPosition, true);
}
var RenderDialog = /*#__PURE__*/forwardRef(function (props, ref) {
  var _useLocaleReceiver = useLocaleReceiver("dialog"),
    _useLocaleReceiver2 = _slicedToArray(_useLocaleReceiver, 1),
    local = _useLocaleReceiver2[0];
  var prefixCls = props.prefixCls,
    attach = props.attach,
    visible = props.visible,
    mode = props.mode,
    zIndex = props.zIndex,
    showOverlay = props.showOverlay,
    _props$onEscKeydown = props.onEscKeydown,
    onEscKeydown = _props$onEscKeydown === void 0 ? noop : _props$onEscKeydown,
    _props$onClosed = props.onClosed,
    onClosed = _props$onClosed === void 0 ? noop : _props$onClosed,
    _props$onClose = props.onClose,
    onClose = _props$onClose === void 0 ? noop : _props$onClose,
    _props$onCloseBtnClic = props.onCloseBtnClick,
    onCloseBtnClick = _props$onCloseBtnClic === void 0 ? noop : _props$onCloseBtnClic,
    _props$onOverlayClick = props.onOverlayClick,
    onOverlayClick = _props$onOverlayClick === void 0 ? noop : _props$onOverlayClick,
    _props$onConfirm = props.onConfirm,
    onConfirm = _props$onConfirm === void 0 ? noop : _props$onConfirm,
    closeBtn = props.closeBtn,
    closeOnEscKeydown = props.closeOnEscKeydown,
    confirmOnEnter = props.confirmOnEnter,
    closeOnOverlayClick = props.closeOnOverlayClick,
    destroyOnClose = props.destroyOnClose,
    showInAttachedElement = props.showInAttachedElement;
  var wrap = useRef();
  var dialog = useRef();
  var dialogPosition = useRef();
  var maskRef = useRef();
  var portalRef = useRef();
  var contentClickRef = useRef(false);
  var isModal = mode === "modal";
  var isNormal = mode === "normal";
  var canDraggable = props.draggable && mode === "modeless";
  var dialogOpenClass = "".concat(prefixCls, "__").concat(mode);
  useDialogEsc(visible, wrap);
  useDialogLockStyle(props);
  useImperativeHandle(ref, function () {
    return wrap.current;
  });
  useEffect(function () {
    if (visible) {
      if (mousePosition && dialog.current) {
        dialog.current.style.transformOrigin = "".concat(mousePosition.x - dialog.current.offsetLeft, "px ").concat(mousePosition.y - dialog.current.offsetTop, "px");
      }
    }
  }, [visible]);
  var onAnimateLeave = function onAnimateLeave() {
    if (wrap.current) {
      wrap.current.style.display = "none";
    }
    if (!isModal) {
      var style = dialog.current.style;
      style.position = "relative";
      style.left = "unset";
      style.top = "unset";
    }
    onClosed && onClosed();
  };
  var onMaskClick = function onMaskClick(e) {
    if (showOverlay && (closeOnOverlayClick !== null && closeOnOverlayClick !== void 0 ? closeOnOverlayClick : local.closeOnOverlayClick)) {
      if (contentClickRef.current) {
        contentClickRef.current = false;
      } else if (e.target === dialogPosition.current) {
        onOverlayClick({
          e: e
        });
        onClose({
          e: e,
          trigger: "overlay"
        });
      }
    }
  };
  var handleCloseBtnClick = function handleCloseBtnClick(e) {
    onCloseBtnClick({
      e: e
    });
    onClose({
      e: e,
      trigger: "close-btn"
    });
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onEscKeydown({
        e: e
      });
      if (closeOnEscKeydown !== null && closeOnEscKeydown !== void 0 ? closeOnEscKeydown : local.closeOnEscKeydown) {
        onClose({
          e: e,
          trigger: "esc"
        });
      }
    } else if (e.key === "Enter" || e.key === "NumpadEnter") {
      e.stopPropagation();
      if (confirmOnEnter) {
        onConfirm({
          e: e
        });
      }
    }
  };
  var renderDialog = function renderDialog() {
    var dest = {};
    if (props.width !== void 0) {
      dest.width = GetCSSValue(props.width);
    }
    if (props.mode === "normal") {
      dest.zIndex = "auto";
    }
    var footer = props.footer ? /* @__PURE__ */React.createElement("div", {
      className: "".concat(prefixCls, "__footer")
    }, props.footer) : null;
    var header = props.header;
    var body = /* @__PURE__ */React.createElement("div", {
      className: "".concat(prefixCls, "__body")
    }, props.body || props.children);
    var closer = closeBtn && /* @__PURE__ */React.createElement("span", {
      onClick: handleCloseBtnClick,
      className: "".concat(prefixCls, "__close")
    }, closeBtn);
    var validWindow = (typeof window === "undefined" ? "undefined" : _typeof(window)) === "object";
    var screenHeight = validWindow ? window.innerHeight || document.documentElement.clientHeight : void 0;
    var screenWidth = validWindow ? window.innerWidth || document.documentElement.clientWidth : void 0;
    var style = _objectSpread(_objectSpread({}, dest), props.style);
    var dialogOffset = {
      x: 0,
      y: 0
    };
    var onDialogMove = function onDialogMove(e) {
      var _dialog$current = dialog.current,
        style2 = _dialog$current.style,
        offsetWidth = _dialog$current.offsetWidth,
        offsetHeight = _dialog$current.offsetHeight;
      var diffX = e.clientX - dialogOffset.x;
      var diffY = e.clientY - dialogOffset.y;
      if (diffX < 0) diffX = 0;
      if (diffY < 0) diffY = 0;
      if (screenWidth - offsetWidth - diffX < 0) diffX = screenWidth - offsetWidth;
      if (screenHeight - offsetHeight - diffY < 0) diffY = screenHeight - offsetHeight;
      style2.position = "absolute";
      style2.left = "".concat(diffX, "px");
      style2.top = "".concat(diffY, "px");
    };
    var onDialogMoveEnd = function onDialogMoveEnd() {
      dialog.current.style.cursor = "default";
      document.removeEventListener("mousemove", onDialogMove);
      document.removeEventListener("mouseup", onDialogMoveEnd);
    };
    var onDialogMoveStart = function onDialogMoveStart(e) {
      contentClickRef.current = true;
      if (canDraggable && e.currentTarget === e.target) {
        var _dialog$current2 = dialog.current,
          offsetLeft = _dialog$current2.offsetLeft,
          offsetTop = _dialog$current2.offsetTop,
          offsetHeight = _dialog$current2.offsetHeight,
          offsetWidth = _dialog$current2.offsetWidth;
        if (offsetWidth > screenWidth || offsetHeight > screenHeight) return;
        dialog.current.style.cursor = "move";
        var diffX = e.clientX - offsetLeft;
        var diffY = e.clientY - offsetTop;
        dialogOffset = {
          x: diffX,
          y: diffY
        };
        document.addEventListener("mousemove", onDialogMove);
        document.addEventListener("mouseup", onDialogMoveEnd);
      }
    };
    var positionStyle = {};
    if (props.top) {
      var topValue = GetCSSValue(props.top);
      positionStyle.paddingTop = topValue;
    }
    var positionClass = classNames("".concat(prefixCls, "__position"), _defineProperty({}, "".concat(prefixCls, "--top"), !!props.top), "".concat(props.placement && !props.top ? "".concat(prefixCls, "--").concat(props.placement) : ""));
    var dialogElement = /* @__PURE__ */React.createElement("div", {
      className: isNormal ? "" : "".concat(prefixCls, "__wrap")
    }, /* @__PURE__ */React.createElement("div", {
      className: isNormal ? "" : positionClass,
      style: positionStyle,
      onClick: onMaskClick,
      ref: dialogPosition
    }, /* @__PURE__ */React.createElement("div", {
      ref: dialog,
      style: style,
      className: classNames("".concat(prefixCls), "".concat(prefixCls, "--default")),
      onMouseDown: onDialogMoveStart
    }, /* @__PURE__ */React.createElement("div", {
      className: classNames("".concat(prefixCls, "__header"))
    }, header, closer), body, footer)));
    return /* @__PURE__ */React.createElement(CSSTransition, {
      "in": props.visible,
      appear: true,
      mountOnEnter: true,
      unmountOnExit: destroyOnClose,
      timeout: transitionTime,
      classNames: "".concat(prefixCls, "-zoom"),
      onEntered: props.onOpened,
      onExited: onAnimateLeave,
      nodeRef: dialog
    }, dialogElement);
  };
  var renderMask = function renderMask() {
    var maskElement;
    if (showOverlay) {
      maskElement = /* @__PURE__ */React.createElement(CSSTransition, {
        "in": visible,
        appear: true,
        timeout: transitionTime,
        classNames: "".concat(prefixCls, "-fade"),
        mountOnEnter: true,
        unmountOnExit: true,
        nodeRef: maskRef
      }, /* @__PURE__ */React.createElement("div", {
        ref: maskRef,
        className: "".concat(prefixCls, "__mask")
      }));
    }
    return maskElement;
  };
  var render = function render() {
    var style = {};
    if (visible) {
      style.display = "block";
    }
    var wrapStyle = _objectSpread(_objectSpread({}, style), {}, {
      zIndex: zIndex
    });
    var dialogBody = renderDialog();
    var wrapClass = classNames(props.className, "".concat(prefixCls, "__ctx"), !isNormal ? "".concat(prefixCls, "__ctx--fixed") : "", visible ? dialogOpenClass : "", isModal && showInAttachedElement ? "".concat(prefixCls, "__ctx--absolute") : "", props.mode === "modeless" ? "".concat(prefixCls, "__ctx--modeless") : "");
    var dialog2 = /* @__PURE__ */React.createElement("div", {
      ref: wrap,
      className: wrapClass,
      style: wrapStyle,
      onKeyDown: handleKeyDown,
      tabIndex: 0
    }, mode === "modal" && renderMask(), dialogBody);
    var dom = null;
    if (visible || wrap.current) {
      if (attach === "" || isNormal) {
        dom = dialog2;
      } else {
        dom = /* @__PURE__ */React.createElement(CSSTransition, {
          "in": visible,
          appear: true,
          timeout: transitionTime,
          mountOnEnter: true,
          unmountOnExit: destroyOnClose,
          nodeRef: portalRef
        }, /* @__PURE__ */React.createElement(Portal, {
          attach: attach,
          ref: portalRef
        }, dialog2));
      }
    }
    return dom;
  };
  return render();
});
RenderDialog.defaultProps = dialogDefaultProps;

export { RenderDialog as default };
//# sourceMappingURL=RenderDialog.js.map
